// src/contexts/AccountProjectContext.tsx
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useDataProvider } from "react-admin";
import { useCanAccess } from "@react-admin/ra-rbac";

interface Account {
  id: string;
  name: string;
}

interface Project {
  id: string;
  name: string;
  accountId: string;
  typeProject?: string;
  codeProject?: string;
  userIdCNHS?: string;
}

interface ContextProps {
  accountId: string | null;
  setAccountId: (id: string) => void;
  projectId: string | null;
  setProjectId: (id: string) => void;
  accounts: Account[];
  projects: Project[];
  filteredProjects: Project[];
  loading: boolean;
  projectMeta: {
    typeProject?: string;
    codeProject?: string;
    hasUserCNHS?: boolean;
    name?: string;
    
  } | null;
}

const AccountProjectContext = createContext<ContextProps | undefined>(
  undefined
);

const STORAGE_KEY_ACCOUNT = "selectedAccountId";
const STORAGE_KEY_PROJECT = "selectedProject";

export const AccountProjectProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dataProvider = useDataProvider();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [accountIdState, setAccountIdState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY_ACCOUNT) || null;
  });
  const [projectIdState, setProjectIdState] = useState<string | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY_PROJECT);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      return parsed.id || null;
    } catch {
      return null;
    }
  });
  const [projectMeta, setProjectMeta] = useState<ContextProps["projectMeta"]>(
    () => {
      const stored = localStorage.getItem(STORAGE_KEY_PROJECT);
      if (!stored) return null;
      try {
        const parsed = JSON.parse(stored);
        const { typeProject, codeProject, hasUserCNHS, name } = parsed;
        return { typeProject, codeProject, hasUserCNHS, name };
      } catch {
        return null;
      }
    }
  );

  const [loading, setLoading] = useState(true);
  const { isLoading: isLoadingCanAccessAccounts, canAccess: canAccessAccounts } = useCanAccess({
    action: 'list',
    resource: 'accountsMenu',
  });
  const { isLoading: isLoadingCanAccessProjects, canAccess: canAccessProjects } = useCanAccess({
    action: 'list',
    resource: 'projectsMenu',
  });

  const setAccountId = (id: string) => {
    localStorage.setItem(STORAGE_KEY_ACCOUNT, id);
    setAccountIdState(id);
    // Reset project selection to "All"
    localStorage.removeItem(STORAGE_KEY_PROJECT);
    setProjectIdState(null);
    setProjectMeta(null);
  };

  const setProjectId = (id: string | null) => {
    if (id === null) {
      localStorage.removeItem(STORAGE_KEY_PROJECT);
      setProjectIdState(null);
      setProjectMeta(null);
      return;
    }

    const selected = projects.find((p) => p.id === id);
    if (selected) {
      const { typeProject, codeProject, userIdCNHS, name } = selected;
      const storedObject = JSON.stringify({
        id,
        typeProject,
        codeProject,
        hasUserCNHS: userIdCNHS ? true : false,
        name,
      });
      localStorage.setItem(STORAGE_KEY_PROJECT, storedObject);
      setProjectIdState(id);
      setProjectMeta({ typeProject, codeProject, hasUserCNHS: userIdCNHS ? true : false, name });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoadingCanAccessAccounts || !canAccessAccounts || isLoadingCanAccessProjects || !canAccessProjects) return;
        const [accountRes, projectRes] = await Promise.all([
          dataProvider.getList("accountsMenu", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "name", order: "ASC" },
            filter: {},
          }),
          dataProvider.getList("projectsMenu", {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "name", order: "ASC" },
            filter: {},
          }),
        ]);

        const accountsData = accountRes.data;
        const projectsData = projectRes.data;

        setAccounts(accountsData);
        setProjects(projectsData);

        const validAccount = accountsData.find((a) => a.id === accountIdState);
        const accountToUse = validAccount?.id || accountsData[0]?.id || null;
        
        setAccountId(accountToUse);

        const relatedProjects = projectsData.filter(
          (p) => p.accountId === accountToUse
        );
        const validProject = relatedProjects.find(
          (p) => p.id === projectIdState
        );
        const projectToUse = validProject?.id || relatedProjects[0]?.id || null;
        setProjectId(projectToUse);
      } catch (err) {
        console.error("Failed to load accounts or projects", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dataProvider, isLoadingCanAccessAccounts, canAccessAccounts, isLoadingCanAccessProjects, canAccessProjects]);

  const filteredProjects = projects.filter(
    (p) => p.accountId === accountIdState
  );
  return (
    <AccountProjectContext.Provider
      value={{
        accountId: accountIdState,
        setAccountId,
        projectId: projectIdState,
        setProjectId,
        accounts,
        projects,
        filteredProjects,
        loading,
        projectMeta,
      }}
    >
      {children}
    </AccountProjectContext.Provider>
  );
};

export const useAccountProject = () => {
  const context = useContext(AccountProjectContext);
  if (!context) {
    throw new Error(
      "useAccountProject must be used within a AccountProjectProvider"
    );
  }
  return context;
};

export const useCurrentProject = () => {
  const { accountId, projectId, projectMeta, accounts, projects } =
    useAccountProject();

  const currentAccount = accounts.find((a) => a.id === accountId) || null;
  const currentProject = projects.find((p) => p.id === projectId) || null;

  return {
    accountId,
    projectId,
    typeProject: projectMeta?.typeProject,
    codeProject: projectMeta?.codeProject,
    hasUserCNHS: projectMeta?.hasUserCNHS || false,
    name: projectMeta?.name,
    currentAccount,
    currentProject,
  };
};
